import type { FarePriceBreakdown } from "@b2bportal/air-shopping-api";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  type AlgomerchTag,
  CoreFlightsComponent,
  type PlatformComponentProps,
  type Prices,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FlightTime } from "@tenant/flights";
import { AirlineIcon, Icon, usePriceDisplay } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FlightCardSummary.module.scss";
import { useIsPackageIncrementalPricing } from "@/pages/Packages/hooks";

export interface FlightCardSummaryComponentProps
  extends PlatformComponentProps {
  duration: string;
  currentPriceText: string;
  originCode: string;
  destinationCode: string;
  departureTime: string;
  arrivalTime: string;
  brandName: string;
  primaryCarrier: string;
  airlineName: string;
  additionalAirlines?: number;
  onClickTag?: (label: string) => void;
  tags: AlgomerchTag[];
  tagsLimit?: number;
  rewardText?: string;
  flightStops: number;
  fareName?: string;
  currentPrice?: Prices | FarePriceBreakdown;
}

export const FlightCardSummary = ({
  duration,
  originCode,
  destinationCode,
  departureTime,
  arrivalTime,
  primaryCarrier,
  airlineName,
  additionalAirlines,
  flightStops,
  className,
  fareName,
  currentPrice,
  brandName,
}: FlightCardSummaryComponentProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();

  const styles = useFlightStyles(
    CoreFlightsComponent.FlightCardSummary,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreFlightsComponent.FlightCardSummary
  );

  const isIncrementalPricing = useIsPackageIncrementalPricing();

  const { rewardsThenPriceText } = usePriceDisplay({
    prices: currentPrice,
    inline: isDesktopAndUp ? "none" : "all",
    formatOptions: isIncrementalPricing ? { signDisplay: "always" } : undefined,
  });

  return isDesktopAndUp ? (
    // Desktop Flight Card Summary
    <div className={clsx(block, className)}>
      <div className={cn("icon")}>
        <AirlineIcon
          airlineCode={primaryCarrier}
          altText={airlineName}
          className={cn("airline-icon")}
        />
      </div>
      <div className={cn("content")}>
        <FlightTime
          departureTime={departureTime}
          arrivalTime={arrivalTime}
          stops={flightStops}
        />
        <div className={cn("airports")}>
          {originCode}-{destinationCode}
        </div>

        <div className={cn("airline")}>
          {airlineName}
          {(additionalAirlines ?? 0) > 0 ? (
            <span className={cn("additional-airlines")}>
              {" "}
              {t("core-flights.vi.countOther", { count: additionalAirlines })}
            </span>
          ) : null}
        </div>

        <div className={cn("duration")}>
          <Icon iconName={IconNameEnum.timer} />
          {duration}
        </div>

        <div className={cn("stops")}>
          {flightStops === 0
            ? t("stopDetails.nonstop")
            : t("stopDetails.stop", { count: flightStops })}
        </div>
      </div>
    </div>
  ) : (
    // Mobile Flight Card Summary
    <>
      <div className={clsx(block, className)}>
        <div className={cn("content")}>
          <p className={cn("pricing")}>{rewardsThenPriceText}</p>
          <FlightTime
            departureTime={departureTime}
            arrivalTime={arrivalTime}
            stops={flightStops}
          />
          <p className={cn("fare-name-with-airports")}>
            {fareName ?? brandName} - {originCode}-{destinationCode}
          </p>

          <div className={cn("airline")}>
            <AirlineIcon
              airlineCode={primaryCarrier}
              altText={airlineName}
              className={cn("airline-icon")}
            />
            {airlineName}
            {(additionalAirlines ?? 0) > 0 ? (
              <span className={cn("additional-airlines")}>
                {" "}
                {t("core-flights.vi.countOther", { count: additionalAirlines })}
              </span>
            ) : null}
          </div>

          <p className={cn("duration")}>
            <Icon iconName={IconNameEnum.timer} />
            {duration}
          </p>
          <p className={cn("stops")}>
            {flightStops === 0
              ? t("stopDetails.nonstop")
              : t("stopDetails.stop", { count: flightStops })}
          </p>
        </div>
      </div>
    </>
  );
};
